import moment from 'moment'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4maps from '@amcharts/amcharts4/maps'
import * as am4charts from '@amcharts/amcharts4/charts'

import { PAYMENT_SYSTEM_TYPES } from 'app/core/constants'

import am4geodataWorldLow from '@amcharts/amcharts4-geodata/worldLow'
import am4themesAnimated from '@amcharts/amcharts4/themes/animated'

const style = getComputedStyle(document.body)

/**
 * Helper create custom marker for map
 *
 * @param mapPolygon
 * @param info
 * @returns {HTMLDivElement}
 */
function createCustomMarker (mapPolygon, info) {
  const chart = mapPolygon.dataItem.component.chart

  const val = [500, 1000, 5000, 10000, 100000, Infinity].findIndex(m => info.count < m) * 2 + 6

  // create holder
  const holder = document.createElement('div')
  holder.className = 'map-marker'
  holder.setAttribute('data-title', mapPolygon.dataItem.dataContext.name + ': ' + info.count)
  holder.style.marginLeft = '-' + val / 2 + 'px'
  holder.style.marginTop = '-' + val / 2 + 'px'
  holder.style.position = 'absolute'

  // maybe add a link to it?
  if (undefined !== mapPolygon.url) {
    holder.onclick = function () {
      window.location.href = mapPolygon.url
    }
    holder.className += ' map-clickable'
  }

  // create dot
  const dot = document.createElement('div')
  dot.className = 'dot'
  dot.style.animationDuration = (Math.random() * (3000 - 2000) + 2000) + 'ms'
  dot.style.WebkitAnimationDuration = (Math.random() * (3000 - 2000) + 2000) + 'ms'
  dot.style.MozAnimationDuration = (Math.random() * (3000 - 2000) + 2000) + 'ms'

  dot.style.width = val + 16 + 'px'
  dot.style.height = val + 16 + 'px'

  holder.appendChild(dot)

  // create pulse
  const pulse = document.createElement('div')
  pulse.className = 'pulse-marker'

  pulse.style.width = val + 'px'
  pulse.style.height = val + 'px'

  holder.appendChild(pulse)

  // append the marker to the map container
  chart.svgContainer.htmlElement.appendChild(holder)

  return holder
}

/**
 * dashboardMap directive
 * <dashboard-map data-map-id='<map-id>'></dashboard-map>
 *
 * @class dashboardMap
 * @memberof mwApp/dashboard
 * @see mwApp/dashboard
 *
 * @ngdoc directive
 * @name mwApp/dashboard.directive:dashboardMap
 */
/* @ngInject */
export function dashboardMap () {
  return {
    restrict: 'E',
    template: /* html */ '<div></div>',
    replace: true,
    link: ($scope, $el, _attr) => {
      const { mapParam } = _attr
      let mapData = []
      am4core.useTheme(am4themesAnimated)

      const map = am4core.create($el[0], am4maps.MapChart)

      try {
        map.geodata = am4geodataWorldLow
      } catch (e) {
        map.raiseCriticalError(new Error('Map geodata could not be loaded. Please download the latest <a href="https://www.amcharts.com/download/download-v4/">amcharts geodata</a> and extract its contents into the same directory as your amCharts files.'))
      }

      map.projection = new am4maps.projections.Miller()
      map.seriesContainer.draggable = false
      map.seriesContainer.resizable = false
      map.maxZoomLevel = 1

      // Note: hide logo!
      map.tooltipContainer.element.node.style.opacity = '0'
      map.tooltipContainer.element.node.style.visibility = 'hidden'

      // Create map polygon series
      var polygonSeries = map.series.push(new am4maps.MapPolygonSeries())
      polygonSeries.exclude = ['AQ']
      polygonSeries.useGeodata = true

      var template = polygonSeries.mapPolygons.template
      template.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2'))
      template.stroke = am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2'))

      const hs1 = template.states.create('hover')
      hs1.properties.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2-hovered'))
      hs1.properties.stroke = am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2-hovered'))

      polygonSeries.calculateVisualCenter = true
      template.propertyFields.id = 'id'
      template.tooltipPosition = 'fixed'
      /* template.fillOpacity = 1; */

      var measelsSeries = map.series.push(new am4maps.MapPolygonSeries())
      measelsSeries.tooltip.background.fillOpacity = 0
      measelsSeries.tooltip.background.cornerRadius = 20
      measelsSeries.tooltip.autoTextColor = false
      measelsSeries.tooltip.label.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_tertiary'))
      measelsSeries.tooltip.dy = -5

      var measelTemplate = measelsSeries.mapPolygons.template
      measelTemplate.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_background_primary'))
      measelTemplate.strokeOpacity = 0
      measelTemplate.fillOpacity = 0
      measelTemplate.tooltipPosition = 'fixed'

      polygonSeries.events.once('ready', function () {
        mapData.length &&
          polygonSeries.mapPolygons.each(function (mapPolygon) {
            var info = mapData.find((m) => m.countryIsoCode === mapPolygon.id)
            if (info && info.count > 0) {
              var polygon = measelsSeries.mapPolygons.create()

              polygon.multiPolygon = am4maps.getCircle(mapPolygon.visualLongitude, mapPolygon.visualLatitude, Math.max(0.5, Math.log(info.count) * Math.LN10 / 10))
              polygon.tooltipText = mapPolygon.dataItem.dataContext.name + ': ' + info.count

              mapPolygon.dummyData = {
                externalElement: createCustomMarker(mapPolygon, info)
              }

              const xy = map.geoPointToSVG({
                longitude: mapPolygon.visualLongitude,
                latitude: mapPolygon.visualLatitude
              })
              mapPolygon.dummyData.externalElement.style.top = xy.y + 'px'
              mapPolygon.dummyData.externalElement.style.left = xy.x + 'px'
            } else {
              mapPolygon.tooltipText = mapPolygon.dataItem.dataContext.name + ': no data'
              mapPolygon.fillOpacity = 0.9
            }
          })
      })

      $scope.$watch(mapParam, function (data) {
        //
        if (data && Object.prototype.hasOwnProperty.call(data, 'points')) {
          mapData = data.points // Note: mapData from parent context!

          polygonSeries.mapPolygons.each(function (mapPolygon) {
            var info = mapData.find((m) => m.countryIsoCode === mapPolygon.id)
            if (info && info.count > 0) {
              var polygon = measelsSeries.mapPolygons.create()

              polygon.multiPolygon = am4maps.getCircle(mapPolygon.visualLongitude, mapPolygon.visualLatitude, Math.max(0.5, Math.log(info.count) * Math.LN10 / 10))
              polygon.tooltipText = mapPolygon.dataItem.dataContext.name + ': ' + info.count

              mapPolygon.dummyData = {
                externalElement: createCustomMarker(mapPolygon, info)
              }

              const xy = map.geoPointToSVG({
                longitude: mapPolygon.visualLongitude,
                latitude: mapPolygon.visualLatitude
              })
              mapPolygon.dummyData.externalElement.style.top = xy.y + 'px'
              mapPolygon.dummyData.externalElement.style.left = xy.x + 'px'
            } else {
              mapPolygon.tooltipText = mapPolygon.dataItem.dataContext.name + ': no data'
              mapPolygon.fillOpacity = 0.9
            }
          })
        }
      })
    }
  }
}

/**
 * Parse graph line data
 *
 * @param data
 * @returns {*}
 */
function parseGraphData (data) {
  let graphLines = []
  let xAxisValue = ''
  let yAxisValue = ''

  if (data && Object.prototype.hasOwnProperty.call(data, 'graphLines')) {
    graphLines = data.graphLines
    xAxisValue = data.xAxisValue
    yAxisValue = data.yAxisValue
  }

  return {
    lines: graphLines,
    xAxisValue: xAxisValue,
    yAxisValue: yAxisValue
  }
}

function lineColor (line) {
  switch (line) {
    case 'ACTIVE':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-10')
    case 'CCPP':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-12')
    case 'CASH':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-10')
    case 'GR4VY_GATEWAY':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-6')
    case 'TRUE_MONEY':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-7')
    case 'AUTHORIZE_NET':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-5')
    case 'AIRWALLEX':
      return '#81CB96'
    case 'CHECK':
      return '#FEA167'
    case 'EXTERNAL_PAYMENTS':
      return '#FFFFFF'
    case 'GOCARDLESS':
      return '#66CCC2'
    case 'PAYPAL':
      return style.getPropertyValue('--nora_colors_2024_additional_additional-14')
    default:
      return 'red'
  }
}

/**
 * Closest chart row to cursor date (Daily Sales Revenue / Active subscribers line charts).
 *
 * @param {Array<Object>} data
 * @param {Date} date
 * @returns {Object|null}
 */
function findClosestChartRowByDate (data, date) {
  if (!data || !data.length || !date || isNaN(date.getTime())) {
    return null
  }
  const t = date.getTime()
  let best = null
  let bestDiff = Infinity
  for (let i = 0; i < data.length; i++) {
    const row = data[i]
    const xv = row.xValue
    const rt = xv instanceof Date ? xv.getTime() : new Date(xv).getTime()
    if (isNaN(rt)) continue
    const diff = Math.abs(rt - t)
    if (diff < bestDiff) {
      bestDiff = diff
      best = row
    }
  }
  return best
}

/**
 * Single XYCursor HTML tooltip: all line series at hovered date, sorted by value descending (UI-6.3).
 *
 * @param {am4charts.XYChart} chart
 * @param {am4charts.DateAxis} dateAxis
 */
function attachCombinedLineChartTooltip (chart, dateAxis) {
  if (chart.__noraCombinedTooltipAttached) {
    return
  }
  chart.__noraCombinedTooltipAttached = true
  const secondaryRgb = style.getPropertyValue('--nora_colors_2024_text-icon_secondary').trim()
  chart.cursor.events.on('cursorpositionchanged', function () {
    const position = chart.cursor.xPosition
    if (position == null || !chart.data.length) {
      chart.cursor.tooltipHTML = ''
      return
    }
    const date = dateAxis.positionToDate(position)
    const row = findClosestChartRowByDate(chart.data, date)
    if (!row) {
      chart.cursor.tooltipHTML = ''
      return
    }
    const rows = []
    chart.series.each(function (series) {
      if (!(series instanceof am4charts.LineSeries)) return
      const field = series.dataFields.valueY
      if (!field) return
      const raw = row[field]
      if (raw == null || raw === '') return
      const num = Number(raw)
      if (isNaN(num)) return
      const strokeColor = series.stroke
      const hex = strokeColor && strokeColor.hex != null
        ? strokeColor.hex
        : am4core.color(strokeColor).hex
      rows.push({ label: series.name, value: num, color: hex })
    })
    rows.sort(function (a, b) {
      return b.value - a.value
    })
    if (!rows.length) {
      chart.cursor.tooltipHTML = ''
      return
    }
    chart.cursor.tooltipHTML = rows.map(function (r) {
      return '<div style="color:' + secondaryRgb + '"><span style="color:' + r.color + '">●</span> ' +
        r.label + ': <strong>' + r.value + '</strong></div>'
    }).join('')
  })
}

/**
 * dashboardChartLine directive
 * <dashboard-chart-line
 *   data-chart-name='<name>'
 *   data-chart-style='<style points>'>
 *   </dashboard-chart-line>
 *
 * @class dashboardChartLine
 * @memberof mwApp/dashboard
 * @see mwApp/dashboard
 *
 * @ngdoc directive
 * @name mwApp/dashboard.directive:dashboardChartLine
 */
/* @ngInject */
export function dashboardChartLine () {
  return {
    restrict: 'E',
    template: /* html */ '<div></div>',
    replace: true,
    link: ($scope, $el, _attr) => {
      const { chartParam, eventLoad } = _attr
      am4core.useTheme(am4themesAnimated)

      function am4themesMyTheme (target) {
        if (target instanceof am4core.InterfaceColorSet) {
          // Used for button fill
          target.setFor('secondaryButton', am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-3')))
          target.setFor('fill', am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2')))
          target.setFor('secondaryButtonHover', am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-3')))
          target.setFor('secondaryButtonDown', am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-3')))
          target.setFor('secondaryButtonStroke', am4core.color(style.getPropertyValue('--nora_colors_2024_surface_surface-2')))
          target.setFor('primaryButton', am4core.color(style.getPropertyValue('--nora_colors_2024_accent_primary')))
          target.setFor('primaryButtonHover', am4core.color(style.getPropertyValue('--nora_colors_2024_accent_primary')))
          target.setFor('primaryButtonActive', am4core.color(style.getPropertyValue('--nora_colors_2024_accent_primary')))
        }
      }

      am4core.useTheme(am4themesMyTheme)

      let chart = am4core.create($el[0], am4charts.XYChart)

      chart.data = []

      // Create axes
      let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
      dateAxis.renderer.minGridDistance = 50
      dateAxis.renderer.labels.template.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_secondary'))

      // Create series
      function createAxisAndSeries (field, backendCode, yTitle) {
        var valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
        valueAxis.title.text = yTitle
        valueAxis.title.fill = style.getPropertyValue('--nora_colors_2024_text-icon_secondary')
        valueAxis.renderer.minLabelPosition = 0.01
        valueAxis.renderer.labels.template.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_secondary'))

        var series = chart.series.push(new am4charts.LineSeries())
        series.dataFields.valueY = field // visits | yValue
        series.dataFields.dateX = 'xValue' // if not date?
        series.strokeWidth = 2
        series.stroke = am4core.color(lineColor(backendCode))
        series.fill = am4core.color(lineColor(backendCode))
        // series.yAxis = valueAxis
        series.name = PAYMENT_SYSTEM_TYPES[backendCode] || backendCode
        series.tooltip.disabled = true
        series.tensionX = 0.8
        series.legendSettings.labelText = `[${am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_secondary'))}]{name}[/]`

        const bullet = series.bullets.push(new am4charts.CircleBullet())
        bullet.circle.strokeWidth = 2

        valueAxis.renderer.line.strokeOpacity = 0
        valueAxis.renderer.line.strokeWidth = 2
        // valueAxis.renderer.line.stroke = series.stroke
        // valueAxis.renderer.labels.template.fill = series.stroke
        // valueAxis.renderer.opposite = true
        valueAxis.renderer.grid.template.disabled = true
      }

      chart.legend = new am4charts.Legend()
      chart.cursor = new am4charts.XYCursor()

      chart.paddingRight = '3%' // Hack: solution for problem cutting the label from left side

      chart.scrollbarX = new am4core.Scrollbar()

      chart.scrollbarX.startGrip.icon.path = ''
      chart.scrollbarX.startGrip.icon.height = 5
      chart.scrollbarX.endGrip.icon.path = ''
      chart.scrollbarX.endGrip.icon.height = 5
      // chart.scrollbarX.background.fill = am4core.color(style.getPropertyValue('red'))

      $scope.$watch(chartParam, function (data, oldval) {
        if (data) {
          if (oldval) {
            chart.dispose()
            chart = am4core.create($el[0], am4charts.XYChart)

            chart.data = []

            // Create axes
            dateAxis = chart.xAxes.push(new am4charts.DateAxis())
            dateAxis.renderer.minGridDistance = 50
            dateAxis.renderer.labels.template.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_secondary'))

            chart.legend = new am4charts.Legend()
            chart.cursor = new am4charts.XYCursor()

            chart.paddingRight = '3%' // Hack: solution for problem cutting the label from left side

            chart.scrollbarX = new am4core.Scrollbar()

            chart.scrollbarX.startGrip.icon.path = ''
            chart.scrollbarX.startGrip.icon.height = 5
            chart.scrollbarX.endGrip.icon.path = ''
            chart.scrollbarX.endGrip.icon.height = 5
          }
          const chartData = []
          const graphData = parseGraphData(data)

          for (let j = 0; j < graphData.lines.length; j++) {
            const line = graphData.lines[j]
            const field = `yValue_${line.name}`

            for (let i = 0; i < line.points.length; i++) {
              const point = line.points[i]

              if (!chartData[i]) {
                chartData.push({
                  xValue: moment.unix(point.xValue).toDate(),
                  [field]: point.yValue
                })
              } else {
                chartData[i][field] = point.yValue
              }
            }

            chart.data = chartData

            const yTitle = !j ? graphData.yAxisValue : '' // Note: only one label
            createAxisAndSeries(field, line.name, yTitle)
          }

          attachCombinedLineChartTooltip(chart, dateAxis)

          // label for empty data
          if (!graphData.lines.length) {
            var label = chart.createChild(am4core.Label)
            label.text = 'Not found data!'
            label.fill = am4core.color(style.getPropertyValue('--nora_colors_2024_text-icon_quaternary'))
            label.fontSize = 50
            label.align = 'center'
            label.isMeasured = false
            label.x = am4core.percent(50)
            label.horizontalCenter = 'middle'
            label.y = am4core.percent(35)
          }

          $scope.$emit(eventLoad, true)
        }

        // Note: hide logo!
        const chartItem = $el[0]
        const htmlCollectionOfTitles = chartItem.getElementsByTagName('title')

        const arrayOfTitles = [].slice.call(htmlCollectionOfTitles)

        const titleToRemove = arrayOfTitles.filter((item) => {
          return item.textContent.match(/Chart created using amCharts library/i)
        })

        titleToRemove.forEach((item) => {
          item.parentElement.style.opacity = '0'
          item.parentElement.style.visibility = 'hidden'
        })
      })

      $scope.$on('$destroy', function () {
        chart.dispose()
      })
    }
  }
}
